import { cn } from '@atlas/ui-primitives'
import {
	appliesToLabel,
	RULE_ELEMENT_TYPE_LABEL,
	RULE_ELEMENT_TYPE_ORDER,
	type RuleElementType,
} from '@/domain/rules.types'

export interface AppliesToPickerProps {
	/**
	 * Optional because `appliesTo` is a `jsonb` column with no vocabulary or presence
	 * guarantee: a rule row written before the column existed, or any response whose
	 * type over-promises, can hand this `undefined` at runtime. Treated as "applies to
	 * all element types", which is what an absent list means anyway.
	 */
	value: RuleElementType[] | undefined
	onChange: (next: RuleElementType[]) => void
	/** Prefix for the toggle ids, so two pickers can coexist on one page. */
	idPrefix?: string
}

/**
 * Element-type applicability control: which kinds of element a rule governs.
 *
 * Empty is a meaningful state, not an unset one — it means "applies to all element
 * types" — so it is spelled out beneath the toggles rather than left as a blank set.
 * Deselecting everything is therefore a legitimate way to widen a rule, not a
 * validation error.
 */
export function AppliesToPicker({ value, onChange, idPrefix = 'applies-to' }: AppliesToPickerProps) {
	const selected = value ?? []
	const toggle = (t: RuleElementType) => {
		onChange(selected.includes(t) ? selected.filter((v) => v !== t) : [...selected, t])
	}

	return (
		<div>
			<div className="flex flex-wrap gap-2">
				{RULE_ELEMENT_TYPE_ORDER.map((t) => {
					const isSelected = selected.includes(t)
					return (
						<button
							key={t}
							id={`${idPrefix}-${t}`}
							type="button"
							aria-pressed={isSelected}
							onClick={() => toggle(t)}
							className={cn(
								'rounded-ds-full border px-3 py-1.5 text-xs',
								isSelected ? 'border-ds-text ring-1 ring-ds-text' : 'border-ds-border text-ds-text-muted',
							)}
						>
							{RULE_ELEMENT_TYPE_LABEL[t]}
						</button>
					)
				})}
			</div>
			<p className="mt-1.5 text-xs text-ds-text-muted">
				{selected.length === 0 ? 'Applies to all element types.' : `Applies to: ${appliesToLabel(selected)}.`}
			</p>
		</div>
	)
}
